import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  BarChart3, 
  Phone, 
  Calendar, 
  ArrowRight, 
  CheckCircle2,
  TrendingUp,
  Target,
  Zap
} from "lucide-react";

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Users,
      title: "Lead Management",
      description: "Track and manage all your leads in one place with detailed contact information and status tracking."
    },
    {
      icon: Phone,
      title: "Call Tracking",
      description: "Log calls, track responses, and measure interest levels to optimize your outreach strategy."
    },
    {
      icon: Calendar,
      title: "Meeting Scheduler",
      description: "Schedule and track meetings with prospects, never miss a follow-up opportunity."
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Real-time metrics on call rates, conversion rates, and team performance at a glance."
    }
  ];

  const stats = [
    { value: "40%", label: "Increase in conversions" },
    { value: "2x", label: "Faster follow-ups" },
    { value: "100%", label: "Lead visibility" }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Storm Pad
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium">
                <Zap className="w-4 h-4" />
                Sales acceleration platform
              </div>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Close more deals with{" "}
                <span className="text-indigo-600">smarter</span> lead tracking
              </h1>
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                A simple, powerful CRM built for sales teams. Track leads, manage calls, 
                schedule meetings, and watch your conversion rates soar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="border-slate-200 hover:bg-slate-50"
                  data-testid="hero-signin-btn"
                >
                  Sign In
                </Button>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl blur-3xl opacity-20"></div>
              <img 
                src="https://images.unsplash.com/photo-1758873268663-5a362616b5a7" 
                alt="Team collaborating"
                className="relative rounded-2xl shadow-2xl border border-slate-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className={`text-center animate-fade-in stagger-${index + 1}`}
              >
                <div 
                  className="text-4xl font-extrabold text-indigo-600 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {stat.value}
                </div>
                <div className="text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <h2 
              className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Everything you need to close deals
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Streamline your sales process with powerful tools designed for modern sales teams.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`p-6 bg-white rounded-lg border border-slate-100 shadow-sm card-hover animate-fade-in stagger-${index + 1}`}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 
                  className="text-lg font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manager Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-sm font-medium">
                <TrendingUp className="w-4 h-4" />
                For Sales Managers
              </div>
              <h2 
                className="text-3xl md:text-4xl font-bold text-white"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Full visibility into your team's performance
              </h2>
              <ul className="space-y-4">
                {[
                  "View all leads across your entire team",
                  "Real-time analytics dashboard with key metrics",
                  "Manage team members and assign leads",
                  "Export data for reporting and analysis"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                className="bg-white text-slate-900 hover:bg-slate-100"
                data-testid="manager-cta-btn"
              >
                Sign In
              </Button>
            </div>
            <div className="relative animate-fade-in stagger-2">
              <img 
                src="https://images.unsplash.com/photo-1758518729685-f88df7890776" 
                alt="Analytics dashboard"
                className="rounded-2xl shadow-2xl border border-slate-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center animate-fade-in">
          <h2 
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Ready to boost your sales?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join thousands of sales teams already using Storm Pad to close more deals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-200"
              data-testid="cta-login-btn"
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-indigo-600 flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-900">Storm Pad</span>
          </div>
          <p className="text-sm text-slate-500">
            © 2024 Storm Pad. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
